import React, { FC, ReactNode, useRef } from 'react';
import Head from 'next/head';
import { useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import OneColumn from '~/components/Layout/OneColumn';
import usePlanPackagePage from '~/hooks/pages/usePlanPackagePage';
import PlanPackagesStyles from '~/styles/pages/PlanPackagesStyles';
import PlanCards from '~/components/PlanCards';
import Modal from '~/components/Modal';
import { ModalKeys, Product } from '~/types/products';
import Eero from '~/components/Modal/screens/Eero';
import AddOnModal from '~/components/Modal/screens/AddOns';
import AddOnModalV2 from '~/components/Modal/screens/AddOns/v2';
import CompatibilityModal from '~/components/Modal/screens/addOnCompatability';
import UpsellModal from '~/components/Modal/screens/Upsell';
import BroadbandConsumerLabel from '~/components/Modal/screens/BroadbandConsumerLabel';
import AccordionFAQ from '~/components/AccordionFAQ';
import PreampPageBuilder from '~/components/PreampPageBuilder';
import Filters from '~/components/ProductFilters';
import PhoneBannerCTA from '~/components/PhoneBannerCTA';
import AddOn from '~/components/AddOn';
import PlanPackageHeader from '~/components/PageHeader/components/PlanPackageHeader';
import useWindowSize from '~/hooks/useWindowSize';
import { Preamp } from '@red-digital/bricks';
import NextDayBanner from '~/components/Banner/NextDayBanner';
import VRCOffer from '~/components/Modal/screens/VRCOffer';
import GDTBanner from '~/components/GDTBanner';
import { getAddon } from '~/helpers/products';
import NFLSTModal from '~/components/Modal/screens/Nflst';
import WifiSecurityOffer from '~/components/Modal/screens/WifiSecurity';
import ComparePlansModal from '~/components/ComparePlansModal';
import { WHOLE_HOME_WIFI_PLUS } from '~/constants/entityIds';
import { StickyBannerProps } from '~/components/Banner/StickyBanner';

interface Props {
  children?: ReactNode;
  stickyBannerProps?: StickyBannerProps;
  setStickyBannerProps?: (props: StickyBannerProps) => void;
  setVasModal?: (product: Product) => void;
  setClickToToggleNetflixBundle?: (value: boolean) => void;
}

const PlanPackage: FC<Props> = ({ setStickyBannerProps, stickyBannerProps }): JSX.Element => {
  const {
    activeModal,
    activeProducts,
    bannerText,
    bundleCost,
    bundleFeatures,
    carousel,
    contact,
    isCopper,
    productFilters,
    selectedState,
    setSelectedState,
    singleProduct,
    twoProducts,
    handleSelectProduct,
    setActiveModal,
    setCompatibilityModal,
    setUpsellModal,
    setEeroModal,
    setVasModal,
    setBroadbandConsumerLabelModal,
    is200,
    is500,
    is1Gig,
    is2Gig,
    is5Gig,
    is7Gig,
    isCopper2Fiber,
    callInNumber,
    secureProducts,
    videoProducts,
    clickToRevealVas,
    setClickToRevealVas,
    clickToToggleNetflixBundle,
    setClickToToggleNetflixBundle,
    showPlanCardBanner,
  } = usePlanPackagePage();

  const { monarch, products, quiz } = useSelector((store: ReduxState) => store);
  const { cartShowDuringOfficeHours, cartHideOnProductSelection } = monarch;

  const scrollToVASRef = useRef<HTMLElement>(null);

  const handleSelectPlan = (product, forceScroll = false) => {
    setActiveModal();
    handleSelectProduct(product, false);
    if (!clickToRevealVas || forceScroll) scrollToVASRef.current.scrollIntoView({ behavior: 'smooth' });
  };
  const { mobile, xsMobile } = useWindowSize();

  return (
    <Preamp placement="1Gl1gDgro5jYjHsS7hdAZx">
      <OneColumn>
        <Head>
          <title>Product Selection</title>
        </Head>
        <Preamp placement="5fnmDeyhsnCWp9INSDtbev">
          {(metadata) => {
            const configuredAddons = [];
            const allAddOns = [...secureProducts, ...videoProducts];
            allAddOns.forEach((product) => {
              const addon = getAddon(product.displayName);
              const currentAddOn = Object.keys(addon).find((key) => addon[key]);
              if (metadata?.addOns?.[currentAddOn]) {
                const {
                  title = '',
                  shortDescription = '',
                  longDescription = '',
                  longDescriptionLegalKey,
                  order,
                  category,
                  sticker,
                } = metadata?.addOns?.[currentAddOn];
                configuredAddons.push({
                  addOnName: currentAddOn,
                  product,
                  title,
                  shortDescription,
                  longDescription,
                  longDescriptionLegalKey,
                  order,
                  category,
                  sticker,
                });
              }
            });

            if (metadata?.header?.countDownBanner?.enabled === 'true' && stickyBannerProps === null) {
              setStickyBannerProps({
                icon: metadata?.header?.countDownBanner?.bannerIcon,
                endDate: metadata?.header?.countDownBanner?.endDate,
                copy: metadata?.header?.countDownBanner?.copy,
                isBAU: true,
              });
            }

            const { comparisonTool } = metadata;
            const comparisonToolEnabled = comparisonTool?.enabled === 'true';

            return (
              <PlanPackagesStyles twoProducts={twoProducts} metadata={metadata}>
                <PreampPageBuilder
                  placement="5fnmDeyhsnCWp9INSDtbev"
                  components={{
                    header: (
                      <PlanPackageHeader
                        bannerText={bannerText}
                        isCopper={isCopper}
                        contact={contact}
                        isCopper2Fiber={isCopper2Fiber}
                        mobile={mobile}
                        phoneNumberHeaderEnabled={metadata?.phoneNumberHeader?.enabled === 'true'}
                        xsMobile={xsMobile}
                        landingPageProduct={products?.landingPageProduct}
                        landingPageProductAvailable={products?.landingPageProductAvailable}
                        setClickToToggleNetflixBundle={setClickToToggleNetflixBundle}
                        headerBanner={metadata?.header}
                        setVasModal={setVasModal}
                      />
                    ),
                    filters: (
                      <Filters
                        productFilters={productFilters}
                        setOpenModal={() => setActiveModal(ModalKeys.COMPARE_PLANS)}
                        comparisonToolEnabled={comparisonToolEnabled}
                      />
                    ),
                    nextDayInstallBanner: <NextDayBanner />,
                    products: (
                      <PlanCards
                        addPhoneState={productFilters?.addPhoneState}
                        sweetener={productFilters.sweetener}
                        carousel={carousel}
                        activeProducts={activeProducts}
                        handleSelectProduct={handleSelectProduct}
                        selectedState={selectedState}
                        singleProduct={singleProduct}
                        setActiveModal={setActiveModal}
                        bundleCost={bundleCost}
                        bundleFeatures={bundleFeatures}
                        setEeroModal={setEeroModal}
                        setVasModal={setVasModal}
                        setBroadbandConsumerLabelModal={setBroadbandConsumerLabelModal}
                        isCopper={isCopper}
                        is200={is200}
                        is500={is500}
                        is1Gig={is1Gig}
                        is2Gig={is2Gig}
                        is5Gig={is5Gig}
                        is7Gig={is7Gig}
                        isCopper2Fiber={isCopper2Fiber}
                        handleSelectPlan={handleSelectPlan}
                        clickToRevealVas={clickToRevealVas}
                        setClickToRevealVas={setClickToRevealVas}
                        clickToToggleNetflixBundle={clickToToggleNetflixBundle}
                        setClickToToggleNetflixBundle={setClickToToggleNetflixBundle}
                        landingPageProduct={products?.landingPageProduct}
                        ndiBanner={products?.ndiBanner}
                        showPlanCardBanner={showPlanCardBanner}
                        netflixToggleEnabled={metadata?.netflixToggle?.enabled}
                        netflixToggleDefault={metadata?.netflixToggle?.defaultPosition}
                        isQuizSession={quiz?.quizSession}
                        videoProducts={videoProducts}
                      />
                    ),
                    ltoBanner: <div id="ltoBannerPortal" />,
                    phoneBanner: cartShowDuringOfficeHours && !cartHideOnProductSelection && (
                      <PhoneBannerCTA enabled="false" callInNumber={callInNumber} />
                    ),
                    addOns: (
                      <AddOn
                        configuredAddons={configuredAddons}
                        secureProducts={secureProducts}
                        videoProducts={videoProducts}
                        selectedState={selectedState}
                        handleSelectProduct={handleSelectProduct}
                        handleAddHomePhone={productFilters?.handleAddHomePhone}
                        handleAddProductState={productFilters?.handleAddProductState}
                        scrollToVASRef={scrollToVASRef}
                        isCopper={isCopper}
                        setVasModal={setVasModal}
                        clickToRevealVas={clickToRevealVas}
                        ndiBanner={products?.ndiBanner}
                        setCompatibilityModal={setCompatibilityModal}
                        setUpsellModal={setUpsellModal}
                        upsellModalEnabled={metadata?.addOns?.upsellModalEnabled}
                        setActiveModal={setActiveModal}
                      />
                    ),
                    faq: <AccordionFAQ enabled="false" />,
                    gdtBanner: !mobile ? <GDTBanner enabled="false" /> : null,
                  }}
                />
                <Modal visible={Boolean(activeModal)} handleOnClose={() => setActiveModal()}>
                  <Eero
                    selectedProduct={selectedState?.modal}
                    selectedState={selectedState}
                    handleSelectPlan={handleSelectPlan}
                    setActiveModal={setActiveModal}
                    visible={activeModal === ModalKeys.EERO}
                  />
                  {/* Temporary until new modals win/lose */}
                  {metadata?.addOns?.modalsV2Enabled === 'true' &&
                  selectedState?.modal?.productId !== WHOLE_HOME_WIFI_PLUS ? (
                    <AddOnModalV2
                      selectedProduct={selectedState?.modal}
                      setActiveModal={setActiveModal}
                      visible={activeModal === ModalKeys.VAS}
                    />
                  ) : (
                    <AddOnModal
                      selectedProduct={selectedState?.modal}
                      setActiveModal={setActiveModal}
                      visible={activeModal === ModalKeys.VAS}
                    />
                  )}
                  <CompatibilityModal
                    selectedProduct={selectedState?.modal}
                    selectedState={selectedState}
                    setSelectedState={setSelectedState}
                    handleSelectPlan={handleSelectPlan}
                    setActiveModal={setActiveModal}
                    visible={activeModal === ModalKeys.ADD_ON_COMPATIBILITY}
                  />
                  <UpsellModal
                    handleSelectProduct={handleSelectProduct}
                    selectedState={selectedState}
                    setSelectedState={setSelectedState}
                    setActiveModal={setActiveModal}
                    visible={activeModal === ModalKeys.UPSELL}
                  />
                  <BroadbandConsumerLabel
                    selectedProduct={selectedState?.modal}
                    setActiveModal={setActiveModal}
                    visible={activeModal === ModalKeys.BROADBAND_CONSUMER_LABEL}
                  />
                  <VRCOffer
                    handleSelectPlan={handleSelectPlan}
                    setActiveModal={setActiveModal}
                    visible={activeModal === ModalKeys.VRC_OFFER}
                    metadata={metadata}
                  />
                  <WifiSecurityOffer
                    visible={activeModal === ModalKeys.WIFI_SECURE}
                    handleSelectProduct={handleSelectProduct}
                    setActiveModal={setActiveModal}
                    selectedProduct={selectedState?.internet}
                  />
                  <NFLSTModal setActiveModal={setActiveModal} visible={activeModal === ModalKeys.NFLST} />
                  <ComparePlansModal
                    visible={activeModal === ModalKeys.COMPARE_PLANS}
                    setActiveModal={setActiveModal}
                    selectedPlan={selectedState?.internet}
                    activeProducts={activeProducts}
                    handleSelectProduct={handleSelectPlan}
                  />
                </Modal>
              </PlanPackagesStyles>
            );
          }}
        </Preamp>
      </OneColumn>
    </Preamp>
  );
};

PlanPackage.displayName = 'PlanPackage';

export default PlanPackage;
